import { KBA_MAX_RETRIES, STRIPE_MAX_RETRIES } from "~/constants/kba";
import { STRIPE_ERROR_CODES_TO_ADDITIONAL_DETAILS } from "~/constants/stripe";
import { stripeVerifiedStatuses } from "~/types/global";
import type {
  ParticipantStatus,
  ParticipantStatusSummary,
  ParticipantVerification,
} from "~/types/participantStatus";
import { formatDate } from "@/utils/date";

/** Without input for this long a participant reads as idle (matches the API). */
export const PARTICIPANT_IDLE_AFTER_SECONDS = 60;

/** A page silent for this long has lost the participant (matches the API). */
export const PARTICIPANT_DISCONNECTED_AFTER_SECONDS = 90;

/** How often a participant's page checks in. */
export const PARTICIPANT_HEARTBEAT_INTERVAL_MS = 30_000;

const BLOCK_DATE_FORMAT = "MM/DD/YYYY hh:mm A";

const FAILED_STRIPE_STATUSES = ["FAILED", "requires_input", "consent_declined"];

export const AUTH_TYPE_LABELS: Record<string, string> = {
  KBA: "KBA",
  KNOWN: "Personally Known",
  WITNESS: "Credible Witness",
  NONE: "No identification",
};

export const STAGE_LABELS: Record<ParticipantStatus["stage"], string> = {
  link_sent: "Invitation",
  authentication: "Authentication",
  onboarding: "Onboarding",
  waiting_room: "Waiting room",
  in_session: "In session",
  complete: "Complete",
};

export const PROGRESS_LABELS: Record<ParticipantStatus["progress"], string> = {
  waiting_for_input: "Waiting for input",
  ready_to_proceed: "Ready to proceed",
  reviewing_documents: "Reviewing documents",
  signing: "Signing",
  signed: "Signed all fields",
};

export const LINK_STATUS_LABELS: Record<ParticipantStatus["link"]["status"], string> = {
  not_sent: "Invitation not sent",
  sent: "Link sent",
  delivery_failed: "Invitation bounced",
  clicked: "Link clicked",
  never_accessed: "Never accessed",
  expired: "Link expired",
};

export const ACTIVITY_LABELS: Record<ParticipantStatus["activity"]["state"], string> = {
  never_seen: "Not seen yet",
  active: "Active",
  idle: "Idle",
  away: "Tab hidden",
  disconnected: "Disconnected",
};

export const PAGE_LABELS: Record<string, string> = {
  login: "Entering their code",
  onboarding: "Onboarding",
  authentication: "Verifying their identity",
  preview: "Previewing documents",
  waiting_room: "Waiting room",
  room: "Session room",
  denied: "Access denied page",
  complete: "Session complete page",
};

export const TIMELINE_LABELS: Record<string, string> = {
  invite_sent: "Invitation sent",
  invite_delivery_failed: "Invitation bounced",
  link_clicked: "Clicked the invitation link",
  link_expired_access: "Opened an expired link",
  otp_verified: "Entered their access code",
  id_verification_started: "Started the ID check",
  kba_started: "Started a KBA quiz",
  kba_declined: "Declined KBA",
  id_verification_succeeded: "ID verified",
  id_verification_failed: "ID verification failed",
  kba_passed: "Passed KBA",
  kba_failed: "Failed a KBA attempt",
  kba_generation_failed: "KBA could not be generated",
  kba_blocked: "Blocked from KBA",
  reached_waiting_room: "Reached the waiting room",
  joined_call: "Joined the call",
  signed: "Signed",
  left_call: "Left the call",
  device_changed: "Switched device",
  went_idle: "Went idle",
  went_away: "Hid the session tab",
  came_back: "Came back",
};

/**
 * "just now", "12 min ago", "3 h ago", or the date for anything older.
 */
export const timeAgo = (iso: string | null | undefined, now: number): string => {
  if (!iso) {
    return "";
  }
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 45) {
    return "just now";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  return formatDate(iso, BLOCK_DATE_FORMAT);
};

/**
 * How far the signer has got with signing: "1 of 3 fields signed" when fields
 * were handed to them, "2 signatures placed" when they signed open fields, or
 * nothing before their first signature.
 */
export const signingText = (status: ParticipantStatus): string | null => {
  const { assigned = 0, signed = 0, signatures = 0 } = status.signing ?? {};
  if (assigned > 0) {
    return `${Math.min(signed, assigned)} of ${assigned} field${assigned === 1 ? "" : "s"} signed`;
  }
  return signatures > 0 ? `${signatures} signature${signatures === 1 ? "" : "s"} placed` : null;
};

/**
 * What the participant is doing, worded for the notary. In the call a signer
 * is reviewing the documents until they sign, so signing is what this says
 * once it has started.
 */
export const progressLabel = (status: ParticipantStatus, inCall = status.in_call): string => {
  if (status.progress === "signed") {
    return PROGRESS_LABELS.signed;
  }
  const signing = signingText(status);
  if (status.progress === "signing") {
    return signing ? `${PROGRESS_LABELS.signing} · ${signing}` : PROGRESS_LABELS.signing;
  }
  if (inCall) {
    return status.signing?.assigned
      ? `${PROGRESS_LABELS.reviewing_documents} · ${signing}`
      : PROGRESS_LABELS.reviewing_documents;
  }
  return PROGRESS_LABELS[status.progress];
};

/** Whole minutes between a timestamp and now, at least 1. */
export const minutesSince = (iso: string | null | undefined, now: number): number =>
  iso ? Math.max(1, Math.floor((now - new Date(iso).getTime()) / 60_000)) : 0;

export const participantName = (status: Pick<ParticipantStatus, "first_name" | "last_name">): string =>
  [status.first_name, status.last_name].filter(Boolean).join(" ") || "Participant";

/**
 * The participant's activity as of `now`. The API reads it once per fetch,
 * so between fetches a page that stops checking in is caught here.
 */
export const currentActivityState = (
  status: ParticipantStatus,
  now: number,
): ParticipantStatus["activity"]["state"] => {
  const { state, last_seen_at } = status.activity;
  if (!last_seen_at) {
    return "never_seen";
  }
  if (now - new Date(last_seen_at).getTime() > PARTICIPANT_DISCONNECTED_AFTER_SECONDS * 1000) {
    return "disconnected";
  }
  return state === "never_seen" ? "active" : state;
};

const stripeFailureText = (v: ParticipantVerification): string => {
  const attempts = `Attempts: ${v.stripe_identity_attempts ?? 0}`;
  const statusValues: Record<string, string> = {
    requires_input: "Was unable to validate their identity. " + attempts,
    consent_declined: "Declined to grant Stripe consent data. " + attempts,
    FAILED: `ID Verification Failed, Attempts: ${v.stripe_identity_attempts ?? 0}/${STRIPE_MAX_RETRIES}`,
  };
  return statusValues[v.stripe_identity_verified ?? ""] ?? "Unknown Error";
};

/**
 * Where a KBA signer stands on their ID and KBA checks, worded as the KBA
 * badge always has, once they have opened their link.
 */
export const kbaStatusText = (v: ParticipantVerification): string => {
  const stripe = v.stripe_identity_verified;
  const kba = v.kba_identity_verified;
  const stripeVerified = stripe !== null && stripeVerifiedStatuses.includes(stripe);

  if (!stripe && !kba) {
    return "ID & KBA Pending";
  }
  if (stripe && FAILED_STRIPE_STATUSES.includes(stripe)) {
    return stripeFailureText(v);
  }
  if (stripeVerified && !kba && !v.identity_attempts) {
    return "ID Verified, KBA pending";
  }
  if (
    stripeVerified &&
    (v.identity_attempts ?? 0) <= KBA_MAX_RETRIES &&
    !["SUCCESS", "GENERATION_FAILED"].includes(kba ?? "") &&
    !v.kba_block_datetime
  ) {
    return `ID Verified, KBA Attempts: ${v.identity_attempts ?? 0}/${KBA_MAX_RETRIES}`;
  }
  if (kba === "GENERATION_FAILED") {
    return "ID verified, KBA generation failed";
  }
  if (v.kba_block_datetime && kba !== "SUCCESS") {
    return `KBA Failed - Blocked until ${formatDate(v.kba_block_datetime, BLOCK_DATE_FORMAT)}`;
  }
  return "KBA & ID Verified";
};

/**
 * The ID and KBA errors the notary can read about, worded as the KBA badge's
 * error modal always has.
 */
export const verificationErrors = (v: ParticipantVerification): { title: string; message: string }[] => {
  const errors: { title: string; message: string }[] = [];

  if (v.kba_last_error && ["GENERATION_FAILED", "FAILED"].includes(v.kba_identity_verified ?? "")) {
    if (v.kba_last_error.success !== undefined && !v.kba_last_error.success) {
      errors.push({
        title: "KBA Test Failed",
        message: "User did not pass the KBA test. Not enough answers were correct.",
      });
    } else if (v.kba_last_error.errorMessage) {
      errors.push({ title: "KBA Generation Failed", message: v.kba_last_error.errorMessage });
    }
  }

  if (v.stripe_identity_verified === "FAILED") {
    const code = v.stripe_identity_last_error?.code;
    const details =
      (code && STRIPE_ERROR_CODES_TO_ADDITIONAL_DETAILS[code as keyof typeof STRIPE_ERROR_CODES_TO_ADDITIONAL_DETAILS]) ||
      "An unknown error occurred within Stripe while verifying the user's identity.";
    errors.push({
      title: "Stripe ID Verification Failed",
      message: `Stripe Verification Error: ${v.stripe_identity_last_error?.reason ?? "Unknown error."} ${details}${code ? ` (Error code: ${code})` : ""}`,
    });
  }

  return errors;
};

const kbaHasFailed = (v: ParticipantVerification): boolean =>
  (v.stripe_identity_verified !== null && FAILED_STRIPE_STATUSES.includes(v.stripe_identity_verified)) ||
  v.kba_identity_verified === "GENERATION_FAILED" ||
  (Boolean(v.kba_block_datetime) && v.kba_identity_verified !== "SUCCESS");

const authIcon = (status: ParticipantStatus, failed: boolean): string => {
  if (status.auth_type === "KBA") {
    return failed || !status.identity_verified ? "mdi:shield-alert" : "mdi:shield-check";
  }
  if (status.model_type === "witness" || status.auth_type === "WITNESS") {
    return "mdi:account-multiple-check";
  }
  return "mdi:account-check";
};

/**
 * What the participant's badge says at a glance: a colour for how they are
 * doing (green active, yellow idle or waiting, red something is wrong, grey
 * not there yet), a short label and a line of timing.
 *
 * Problems come first, since they are what the notary has to act on; then
 * whether the participant is there at all; then how far they have got.
 */
export const summarizeParticipantStatus = (
  status: ParticipantStatus,
  now: number,
  inCall = status.in_call,
): ParticipantStatusSummary => {
  const v = status.verification;
  const isKba = status.auth_type === "KBA";
  const failed = isKba && kbaHasFailed(v);
  const activity = currentActivityState(status, now);
  const lastActivity = status.activity.last_activity_at ?? status.activity.last_seen_at;
  const icon = authIcon(status, failed);
  const hasError = failed || verificationErrors(v).length > 0 || status.errors.length > 0;

  const summary = (tone: ParticipantStatusSummary["tone"], label: string, detail: string | null = null) => ({
    tone,
    icon,
    label,
    detail,
    hasError,
  });

  if (status.stage === "complete") {
    return summary("green", "Session complete");
  }

  // In the call, sitting still is what a signer does while the notary talks,
  // so idle says nothing worth a colour there; and one who has signed all
  // their fields is done whatever else they are doing. A hidden tab is the one
  // thing worth flagging, since they can't see the document, and the badge
  // says that is why.
  if (inCall) {
    const progress = progressLabel(status, true);
    if (activity === "away" && status.progress !== "signed") {
      return summary("yellow", "In session", `Session tab hidden · ${progress}`);
    }
    return summary("green", "In session", progress);
  }

  const link = status.link;
  if (link.status === "expired") {
    return summary("red", LINK_STATUS_LABELS.expired, link.expires_at ? `Expired ${timeAgo(link.expires_at, now)}` : null);
  }
  if (link.status === "delivery_failed") {
    return summary("red", LINK_STATUS_LABELS.delivery_failed, "Check the email address or share the link another way");
  }

  if (failed) {
    return summary("red", kbaStatusText(v), lastActivity ? `Last activity ${timeAgo(lastActivity, now)}` : null);
  }

  if (activity === "disconnected") {
    return summary("red", "Client disconnected", `Last seen ${timeAgo(status.activity.last_seen_at, now)}`);
  }

  if (activity === "never_seen") {
    switch (link.status) {
      case "not_sent":
        return summary("gray", LINK_STATUS_LABELS.not_sent);
      case "sent":
        return summary("gray", LINK_STATUS_LABELS.sent, link.invite_sent_at ? `Sent ${timeAgo(link.invite_sent_at, now)}` : null);
      case "never_accessed":
        return summary(
          "yellow",
          LINK_STATUS_LABELS.never_accessed,
          link.invite_sent_at ? `Link sent ${timeAgo(link.invite_sent_at, now)}` : "Invitation link not clicked",
        );
      default:
        return summary(
          "yellow",
          LINK_STATUS_LABELS.clicked,
          link.clicked_at ? `Clicked link ${timeAgo(link.clicked_at, now)}` : null,
        );
    }
  }

  const progress =
    status.progress === "signing" || status.progress === "signed"
      ? progressLabel(status, false)
      : status.stage === "authentication" && isKba && v.token_verify
      ? kbaStatusText(v)
      : status.stage === "waiting_room"
        ? PROGRESS_LABELS.ready_to_proceed
        : status.activity.page
          ? (PAGE_LABELS[status.activity.page] ?? STAGE_LABELS[status.stage])
          : STAGE_LABELS[status.stage];

  if (activity === "idle") {
    return summary("yellow", `Client idle (${minutesSince(lastActivity, now)} min)`, progress);
  }
  if (activity === "away") {
    return summary("yellow", "Session tab hidden", progress);
  }

  return summary(
    "green",
    progress,
    status.device?.changed_at && now - new Date(status.device.changed_at).getTime() < 10 * 60_000
      ? `Switched device ${timeAgo(status.device.changed_at, now)}`
      : lastActivity
        ? `Last activity ${timeAgo(lastActivity, now)}`
        : null,
  );
};

/**
 * What the notary could do to help the participant along, from where they
 * stand right now.
 */
export const participantInsights = (status: ParticipantStatus, now: number, inCall = status.in_call): string[] => {
  const insights: string[] = [];
  const activity = currentActivityState(status, now);
  const v = status.verification;

  if (inCall) {
    return insights;
  }

  switch (status.link.status) {
    case "not_sent":
      insights.push("Their invitation hasn't been sent yet. Use Invite in the menu bar to send them the link.");
      break;
    case "sent":
    case "never_accessed":
      if (activity === "never_seen") {
        insights.push("They haven't opened their link. Ask them to check their spam folder, or resend the link by SMS.");
      }
      break;
    case "delivery_failed":
      insights.push("Their invitation email bounced. Check the email address on file, or send the link by SMS.");
      break;
    case "expired":
      insights.push("Their link's time window has closed. Update the session time or the participant to give them a new window.");
      break;
  }

  if (activity === "disconnected") {
    insights.push("Their page stopped responding. Ask them to reload the page or reopen their link.");
  } else if (activity === "idle" || activity === "away") {
    insights.push("They may have switched tabs or stepped away. A quick message in the chat can bring them back.");
  }

  if (status.device?.changed_at) {
    insights.push(
      `They switched to another device${status.device.browser ? ` (${status.device.browser} on ${status.device.os})` : ""}. If audio or video fails, ask them to stay on one device.`,
    );
  }

  if (status.auth_type === "KBA") {
    if (v.kba_block_datetime && v.kba_identity_verified !== "SUCCESS") {
      insights.push(
        `They used up their KBA attempts and are blocked until ${formatDate(v.kba_block_datetime, BLOCK_DATE_FORMAT)}. The block lifts on its own; you can also verify them another way.`,
      );
    } else if (v.kba_identity_verified === "GENERATION_FAILED") {
      insights.push("No KBA quiz could be generated for them. Check that their name, address, DOB and SSN are correct.");
    } else if (v.stripe_identity_verified && FAILED_STRIPE_STATUSES.includes(v.stripe_identity_verified)) {
      insights.push("Their ID check didn't pass. They can retry with a clearer photo of a valid ID in good lighting.");
    }
  }

  if (status.stage === "waiting_room" && activity !== "disconnected") {
    insights.push("They're in the waiting room and ready to be brought into the session.");
  }

  return insights;
};
